/* eslint-disable jsx-a11y/anchor-is-valid */
import {useEffect} from 'react'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useNavigate} from 'react-router-dom'
import {PasswordMeterComponent} from '../../../template/assets/ts/components'
import {authAPI} from '../../../services/AuthService'
import {phoneValidate} from '../../../template/helpers/components/PhoneValidate'
import {useIntl} from 'react-intl'

const initialValues = {
  fullname: '',
  phone: '',
  company: '',
  email: '',
  password: '',
  changepassword: '',
  acceptTerms: false,
}

const registrationSchema = (f: any) =>
  Yup.object().shape({
    fullname: Yup.string()
      .min(3, f({id: 'FORMIK.ERROR.MIN'}))
      .max(50, f({id: 'FORMIK.ERROR.MAX'}))
      .required(f({id: 'AUTH.SIGNUP.FULLNAME'}) + f({id: 'FORMIK.ERROR.REQUIREDFIELD'})),
    email: Yup.string()
      .email(f({id: 'EMAIL.FORMAT.ERROR'}))
      .min(3, f({id: 'FORMIK.ERROR.MIN'}))
      .max(50, f({id: 'FORMIK.ERROR.MAX'}))
      .required(f({id: 'EMAIL.LABEL'}) + f({id: 'FORMIK.ERROR.REQUIREDFIELD'})),
    phone: Yup.string()
      .min(3, f({id: 'FORMIK.ERROR.MIN'}))
      .max(50, f({id: 'FORMIK.ERROR.MAX'}))
      .required(f({id: 'AUTH.SIGNUP.PHONE'}) + f({id: 'FORMIK.ERROR.REQUIREDFIELD'})),
    password: Yup.string()
      .min(8, f({id: 'FORMIK.ERROR.MIN.8'}))
      .max(35, f({id: 'FORMIK.ERROR.MAX.35'}))
      .required(f({id: 'PASSWORD.LABEL'}) + f({id: 'FORMIK.ERROR.REQUIREDFIELD'})),
    changepassword: Yup.string()
      .required(f({id: 'AUTH.SIGNUP.PASSWORD.CONFIRM.PLACEHOLDER'}) + f({id: 'FORMIK.ERROR.REQUIREDFIELD'}))
      .when('password', {
        is: (val: string) => (val && val.length > 0 ? true : false),
        then: Yup.string().oneOf([Yup.ref('password')], f({id: 'AUTH.SIGNUP.PASSWORD.CONFIRM.ERROR'})),
      }),
    acceptTerms: Yup.bool().required(f({id: 'AUTH.SIGNUP.AGREE.ERROR'})),
  })

export function Registration() {
  const intl = useIntl()
  const navigate = useNavigate()
  const [register, {isLoading: loading, isError}] = authAPI.useRegisterMutation()

  const formik = useFormik({
    initialValues,
    validationSchema: registrationSchema(intl.formatMessage),
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      await register({
        email: values.email,
        phone: values.phone,
        fullname: values.fullname,
        company: values.company,
        password: values.password,
        confirm_password: values.changepassword,
      })
        .unwrap()
        .then((_) => {
          setStatus(intl.formatMessage({id: 'AUTH.SIGNUP.SUCCESS'}) + values.email)
        })
        .catch((err) => {
          setSubmitting(false)
          if (err.data.detail === 'the user with this email already exists in the system.') {
            setStatus(intl.formatMessage({id: 'AUTH.SIGNUP.ERROR'}))
          } else {
            setStatus(err.data.detail)
          }
        })
        .finally(() => {
          setTimeout(() => {
            navigate('/login', {replace: true})
          }, 7000)
        })
    },
  })

  useEffect(() => {
    PasswordMeterComponent.bootstrap()
  }, [])

  return (
    <form
      className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
      noValidate
      id='kt_login_signup_form'
      onSubmit={formik.handleSubmit}
    >
      <div className='mb-10 text-center'>
        <h1 className='text-dark mb-3 fs-5'>{intl.formatMessage({id: 'AUTH.SIGNUP.TITLE'})}</h1>

        <div className='text-gray-500 fs-5'>
          {intl.formatMessage({id: 'AUTH.SIGNUP.HAVEACCOUNT.DESSCRIPTION'})}
          <Link to='/auth/login' className='link-primary fw-bolder' style={{marginLeft: '5px'}}>
            {intl.formatMessage({id: 'PASSWORD.FORGOT'})}?
          </Link>
        </div>
      </div>

      {formik.status && (
        <>
          {isError && (
            <div className='mb-lg-15 alert alert-danger'>
              <div className='alert-text font-weight-bold'>{formik.status}</div>
            </div>
          )}
          {!isError && (
            <div className='mb-lg-15 alert alert-primary'>
              <div className='alert-text font-weight-bold'>{formik.status}</div>
            </div>
          )}
        </>
      )}

      <div className='row fv-row mb-7'>
        <div className='col-xl-6'>
          <label className='form-label fw-bolder text-dark fs-6'>
            {intl.formatMessage({id: 'AUTH.SIGNUP.FULLNAME'})}
          </label>
          <input
            placeholder={intl.formatMessage({id: 'AUTH.SIGNUP.FULLNAME.PLACEHOLDER'})}
            type='text'
            autoComplete='off'
            {...formik.getFieldProps('fullname')}
            className={clsx(
              'form-control form-control-lg form-control-solid',
              {
                'is-invalid': formik.touched.fullname && formik.errors.fullname,
              },
              {
                'is-valid': formik.touched.fullname && !formik.errors.fullname,
              }
            )}
          />
          {formik.touched.fullname && formik.errors.fullname && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.fullname}</span>
              </div>
            </div>
          )}
        </div>
        <div className='col-xl-6'>
          <div className='fv-row mb-5'>
            <label className='form-label fw-bolder text-dark fs-6'>
              {intl.formatMessage({id: 'AUTH.SIGNUP.PHONE'})}
            </label>
            <input
              placeholder={intl.formatMessage({id: 'AUTH.SIGNUP.PHONE.PLACEHOLDER'})}
              type='text'
              autoComplete='off'
              onInput={(e: any) => {
                e.target.value = phoneValidate(e.target.value)
              }}
              {...formik.getFieldProps('phone')}
              className={clsx(
                'form-control form-control-lg form-control-solid',
                {
                  'is-invalid': formik.touched.phone && formik.errors.phone,
                },
                {
                  'is-valid': formik.touched.phone && !formik.errors.phone,
                }
              )}
            />
            {formik.touched.phone && formik.errors.phone && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.phone}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className='fv-row mb-7'>
        <label className='form-label fw-bolder text-dark fs-6'>{intl.formatMessage({id: 'AUTH.SIGNUP.COMPANY'})}</label>
        <input
          placeholder={intl.formatMessage({id: 'AUTH.SIGNUP.COMPANY.PLACEHOLDER'})}
          type='email'
          autoComplete='off'
          {...formik.getFieldProps('company')}
          className={clsx('form-control form-control-lg form-control-solid')}
        />
        {formik.touched.company && formik.errors.company && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.company}</span>
            </div>
          </div>
        )}
      </div>

      <div className='fv-row mb-7'>
        <label className='form-label fw-bolder text-dark fs-6'>{intl.formatMessage({id: 'EMAIL.LABEL'})}</label>
        <input
          placeholder={intl.formatMessage({id: 'EMAIL.PLACEHOLDER'})}
          type='email'
          autoComplete='off'
          {...formik.getFieldProps('email')}
          className={clsx(
            'form-control form-control-lg form-control-solid',
            {'is-invalid': formik.touched.email && formik.errors.email},
            {
              'is-valid': formik.touched.email && !formik.errors.email,
            }
          )}
        />
        {formik.touched.email && formik.errors.email && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.email}</span>
            </div>
          </div>
        )}
      </div>

      <div className='mb-10 fv-row' data-kt-password-meter='true'>
        <div className='mb-1'>
          <label className='form-label fw-bolder text-dark fs-6'>{intl.formatMessage({id: 'PASSWORD.LABEL'})}</label>
          <div className='position-relative mb-3'>
            <input
              type='password'
              placeholder={intl.formatMessage({id: 'PASSWORD.PLACEHOLDER'})}
              autoComplete='on'
              {...formik.getFieldProps('password')}
              className={clsx(
                'form-control form-control-lg form-control-solid',
                {
                  'is-invalid': formik.touched.password && formik.errors.password,
                },
                {
                  'is-valid': formik.touched.password && !formik.errors.password,
                }
              )}
            />
            {formik.touched.password && formik.errors.password && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.password}</span>
                </div>
              </div>
            )}
          </div>
          <div className='d-flex align-items-center mb-3' data-kt-password-meter-control='highlight'>
            <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
            <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
            <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px me-2'></div>
            <div className='flex-grow-1 bg-secondary bg-active-success rounded h-5px'></div>
          </div>
        </div>
        <div className='text-muted'>{intl.formatMessage({id: 'AUTH.SIGNUP.PASSWORD.DESCRIPTION'})}</div>
      </div>

      <div className='fv-row mb-5'>
        <label className='form-label fw-bolder text-dark fs-6'>
          {intl.formatMessage({id: 'AUTH.SIGNUP.PASSWORD.CONFIRM'})}
        </label>
        <input
          type='password'
          placeholder={intl.formatMessage({id: 'AUTH.SIGNUP.PASSWORD.CONFIRM.PLACEHOLDER'})}
          autoComplete='off'
          {...formik.getFieldProps('changepassword')}
          className={clsx(
            'form-control form-control-lg form-control-solid',
            {
              'is-invalid': formik.touched.changepassword && formik.errors.changepassword,
            },
            {
              'is-valid': formik.touched.changepassword && !formik.errors.changepassword,
            }
          )}
        />
        {formik.touched.changepassword && formik.errors.changepassword && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.changepassword}</span>
            </div>
          </div>
        )}
      </div>

      <div className='fv-row mb-10'>
        <div className='form-check form-check-custom form-check-solid'>
          <input
            className='form-check-input'
            type='checkbox'
            id='kt_login_toc_agree'
            {...formik.getFieldProps('acceptTerms')}
          />
          <label className='form-check-label fw-bold text-gray-700 fs-6' htmlFor='kt_login_toc_agree'>
            {intl.formatMessage({id: 'AUTH.SIGNUP.AGREE'})}
            <a href='https://callbee.io/doc/personal_data_license.pdf'>
              {intl.formatMessage({id: 'AUTH.SIGNUP.AGREE.PERSONALDATA'})}
            </a>
            {intl.formatMessage({id: 'AUTH.SIGNUP.AGREE.AND'})}
            <a href='https://callbee.io/doc/license_agreement.pdf'>
              {intl.formatMessage({id: 'AUTH.SIGNUP.AGREE.LICENSEAGREEMENT'})}
            </a>
            .
          </label>
          {/* <Link to='/auth/terms' className='ms-1 link-primary'>
              terms and conditions
            </Link> */}
          {formik.touched.acceptTerms && formik.errors.acceptTerms && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.acceptTerms}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className='text-center'>
        <button
          type='submit'
          id='kt_sign_up_submit'
          className='btn btn-lg btn-primary w-100 mb-5'
          disabled={formik.isSubmitting || !formik.isValid || !formik.values.acceptTerms}
        >
          {!loading && <span className='indicator-label'>{intl.formatMessage({id: 'BUTTON.SUBMIT'})}</span>}
          {loading && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Please wait... <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
        <Link to='/auth/login'>
          <button
            type='button'
            id='kt_login_signup_form_cancel_button'
            className='btn btn-lg btn-light-primary w-100 mb-5'
          >
            {intl.formatMessage({id: 'BUTTON.CANCEL'})}
          </button>
        </Link>
      </div>
    </form>
  )
}
